import { useCallback, useState } from 'react'

/**
 * Decides whether the CDN-hosted branding logo should be rendered.
 *
 * The bundled logo is a CSS background (one asset per breakpoint), so there is no default `src`
 * to hand back here: `isRemote === false` means "leave the stylesheet alone". A missing config,
 * a disabled flag, or a failed load (404/network/timeout) all fall back to that bundled logo,
 * so the login page can never end up with a broken image.
 *
 * `isLoaded` only turns true once the remote image has decoded. Callers keep both logos hidden
 * until then, so the page never paints the bundled logo and swaps it for the remote one.
 *
 * Load state is tracked per url rather than as a flag, so a config/flag change invalidates it
 * without a reset effect that would race the `imageRef` callback below.
 */
const useDynamicLogo = (remoteSrc, isEnabled) => {
  const [loadedSrc, setLoadedSrc] = useState(undefined)
  const [failedSrc, setFailedSrc] = useState(undefined)

  const isRemote = Boolean(isEnabled) && Boolean(remoteSrc) && failedSrc !== remoteSrc

  const onLoad = useCallback(() => setLoadedSrc(remoteSrc), [remoteSrc])

  const onError = useCallback(() => setFailedSrc(remoteSrc), [remoteSrc])

  // A cached image finishes loading before React attaches `onLoad`, which would leave the logo
  // hidden for good. Refs attach before that can happen, so settle it from `complete` here.
  const imageRef = useCallback(node => {
    if (!node || !node.complete) {
      return
    }

    if (node.naturalWidth > 0) {
      setLoadedSrc(node.currentSrc || node.src)
    } else {
      setFailedSrc(node.currentSrc || node.src)
    }
  }, [])

  return {
    src: isRemote ? remoteSrc : undefined,
    imageRef,
    onLoad,
    onError,
    isRemote,
    isLoaded: isRemote && loadedSrc === remoteSrc,
  }
}

export default useDynamicLogo
